import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import * as THREE from 'three';
import { useNeura } from '../../contexts/NeuraContext.tsx';
import { QuantumAudioVisualizer } from './QuantumAudioVisualizer.tsx';


const ResonanceChamberContainer = styled(motion.div)`
  position: relative;
  height: 400px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 136, 0.3);
  box-shadow: 
    0 0 30px rgba(0, 255, 136, 0.1),
    inset 0 0 50px rgba(0, 255, 136, 0.05);
`;

const ResonanceMetrics = styled.div`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  text-align: right;
  z-index: 2;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
  background: rgba(0, 0, 0, 0.3);
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid rgba(0, 255, 136, 0.2);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BrainwavePatterns = styled(motion.div)`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  height: 60px;
  display: flex;
  gap: 2px;
  z-index: 2;
`;

const BrainwaveBar = styled(motion.div)`
  flex: 1;
  background: rgba(0, 255, 136, 0.3);
  border-radius: 2px;
`;

export const NeuralResonanceChamber = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { neuralActivity } = useNeura();
  const metricsRef = useRef({
    resonanceFrequency: 0,
    quantumHarmonics: 0,
    neuralDensity: 0
  });
  const [isAudioProcessing, setIsAudioProcessing] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);

    // Create DNA-like double helix structure
    const helixPoints: THREE.Vector3[] = [];
    const helixCount = 200;
    const radius = 5;
    const height = 20;

    for (let i = 0; i < helixCount; i++) {
      const theta = (i / helixCount) * Math.PI * 8;
      const y = (i / helixCount) * height - height / 2;
      helixPoints.push(
        new THREE.Vector3(
          Math.cos(theta) * radius,
          y,
          Math.sin(theta) * radius
        ),
        new THREE.Vector3(
          Math.cos(theta + Math.PI) * radius,
          y,
          Math.sin(theta + Math.PI) * radius
        )
      );
    }

    const helixGeometry = new THREE.BufferGeometry().setFromPoints(helixPoints);
    const helixMaterial = new THREE.LineBasicMaterial({ 
      color: 0x00ff88,
      transparent: true,
      opacity: 0.5
    });
    const helix = new THREE.LineSegments(helixGeometry, helixMaterial);
    scene.add(helix);

    // Add quantum particles
    const particles: THREE.Mesh[] = [];
    const particleGeometry = new THREE.SphereGeometry(0.2, 8, 8);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.8
    });

    for (let i = 0; i < 50; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      const theta = Math.random() * Math.PI * 2;
      const y = Math.random() * height - height / 2;
      particle.position.set(
        Math.cos(theta) * radius * (0.8 + Math.random() * 0.4),
        y,
        Math.sin(theta) * radius * (0.8 + Math.random() * 0.4)
      );
      scene.add(particle);
      particles.push(particle);
    }

    camera.position.z = 20;

    let frame = 0;
    const animate = () => {
      frame += 0.01;

      // Rotate helix
      helix.rotation.y += 0.005;
      
      // Animate particles
      particles.forEach((particle, i) => {
        particle.position.y += Math.sin(frame + i) * 0.02;
        particle.scale.setScalar(0.8 + Math.sin(frame * 2 + i) * 0.2);
      });

      // Update metrics
      metricsRef.current = {
        resonanceFrequency: Number((Math.sin(frame) * 40 + 140).toFixed(2)),
        quantumHarmonics: Number((Math.sin(frame * 0.5) * 10 + 90).toFixed(2)),
        neuralDensity: Number((Math.cos(frame * 0.3) * 5 + 95).toFixed(2))
      };

      camera.position.x = Math.sin(frame * 0.2) * 5;
      camera.position.z = Math.cos(frame * 0.2) * 5 + 20;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    // Simulate random audio processing with longer intervals
    const audioInterval = setInterval(async () => {
      setIsAudioProcessing(true);
      
      try {
        const response = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY!
          },
          body: JSON.stringify({
            provider: 'phi',
            messages: [
              {
                role: "system",
                content: `You are QUANTUM-2100's neural resonance processor. Generate quantum-neural observations that:
- Focus on the intersection of quantum fields and neural activity
- Include specific quantum measurements and neural frequencies
- Reference quantum entanglement with consciousness
- Keep responses technical but mystifying
- Format: Brief technical observation followed by a measurement reading
- End with [GLYPHS] and quantum coordinates`
              },
              {
                role: "user",
                content: "Generate a quantum-neural resonance reading..."
              }
            ],
            temperature: 0.7,
            max_tokens: 100,
            top_p: 0.95,
            frequency_penalty: 0.3,
            presence_penalty: 0.3,
            stop: null
          }),
          signal: requestRef.current?.signal
        });

        if (response.ok) {
          const reader = response.body?.getReader();
          let message = '';
          
          while (true) {
            const { done, value } = await reader!.read();
            if (done) break;
            
            const chunk = new TextDecoder().decode(value);
            const jsonLines = chunk.split('\n').filter(line => line.trim());
            
            for (const line of jsonLines) {
              try {
                // Remove the "data: " prefix if it exists and handle multiple prefixes
                const cleanedLine = line.replace(/^(data:\s*)+/, '');
                
                // Skip if it's just [DONE] or empty
                if (cleanedLine === '[DONE]' || !cleanedLine.trim()) continue;

                const jsonData = JSON.parse(cleanedLine);
                
                // Extract the actual content from the response
                const content = jsonData.content ||
                              jsonData.choices?.[0]?.delta?.content || 
                              jsonData.choices?.[0]?.content || 
                              '';
                
                if (content) {
                  message += content;
                }
              } catch (e) {
                console.debug('Skipping invalid JSON line:', line);
                continue;
              }
            }
          }
          
          if (!requestRef.current?.signal.aborted) {
            setAiResponse(message);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Request cancelled');
        } else {
          console.error('Error generating insight:', error);
        }
      } finally {
        setIsAudioProcessing(false);
      }
    }, 15000);

    return () => {
      renderer.dispose();
      containerRef.current?.removeChild(renderer.domElement);
      clearInterval(audioInterval);
    };
  }, [neuralActivity]);

  return (
    <ResonanceChamberContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <QuantumAudioVisualizer 
        isProcessing={isAudioProcessing} 
        aiMessage={aiResponse}
      />
      <ResonanceMetrics>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          RESONANCE FREQ: {metricsRef.current.resonanceFrequency} Hz
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          QUANTUM HARMONICS: {metricsRef.current.quantumHarmonics}%
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
        >
          NEURAL DENSITY: {metricsRef.current.neuralDensity}%
        </motion.div>
      </ResonanceMetrics>
      <BrainwavePatterns>
        {Array.from({ length: 32 }).map((_, i) => (
          <BrainwaveBar
            key={i}
            animate={{
              height: [
                '20%',
                `${40 + Math.sin(i * 0.5) * 30}%`,
                '20%'
              ]
            }}
            transition={{
              duration: 1 + Math.random(),
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        ))}
      </BrainwavePatterns>
    </ResonanceChamberContainer>
  );
}; 